import {
  AssignmentException,
  BenefitProgram,
  BrowseAndGoState,
  Merchant,
  MerchantRate,
  MultiChoiceCardModel,
  OperationModel,
  SavedSelection
} from './types'

const CATEGORIES = [
  'Alimentation',
  'Restauration',
  'Mode',
  'Beauté',
  'Culture',
  'High-tech',
  'Maison',
  'Jardinerie',
  'Sport',
  'Loisirs'
]

const BASE_MERCHANTS: Merchant[] = [
  { id: 'boulanger', name: 'Boulanger', categories: ['High-tech', 'Maison'], bank: false },
  { id: 'carrefour', name: 'Carrefour', categories: ['Alimentation', 'Maison'], bank: false },
  { id: 'cultura', name: 'Cultura', categories: ['Culture', 'Loisirs'], bank: false },
  { id: 'decathlon', name: 'Decathlon', categories: ['Sport', 'Loisirs'], bank: false },
  { id: 'fnac', name: 'Fnac Darty', categories: ['Culture', 'High-tech', 'Maison'], bank: false },
  { id: 'galeries', name: 'Galeries Lafayette', categories: ['Mode', 'Beauté', 'Maison'], bank: false },
  { id: 'ikea', name: 'IKEA', categories: ['Maison'], bank: false },
  { id: 'kiabi', name: 'Kiabi', categories: ['Mode'], bank: false },
  { id: 'leroy', name: 'Leroy Merlin', categories: ['Maison', 'Jardinerie'], bank: false },
  { id: 'redoute', name: 'La Redoute', categories: ['Mode', 'Maison'], bank: false },
  { id: 'sephora', name: 'Sephora', categories: ['Beauté'], bank: false },
  { id: 'cf-cadeau', name: 'Carte cadeau Carrefour', categories: ['Alimentation', 'Maison'], bank: false },
  { id: 'cf-ticket', name: 'Carrefour Spectacle by Ticketmaster', categories: ['Culture', 'Loisirs'], bank: false },
  { id: 'cf-spectacle', name: 'Carte cadeau Carrefour Spectacle', categories: ['Culture', 'Loisirs'], bank: false },
  { id: 'ew-culture', name: 'Everywish Culture', categories: ['Culture'], bank: false },
  { id: 'ew-noel', name: 'Everywish Noël', categories: ['Loisirs'], bank: false },
  { id: 'ew-rentree', name: 'Everywish Rentrée', categories: ['Culture', 'Mode'], bank: false }
]

const URSSAF_EBONS = ['fnac', 'decathlon', 'cultura', 'cf-cadeau', 'ew-noel']

interface OperationSeed {
  id: string
  name: string
  code: string
  collectsChildIdentity?: boolean
}

const URSSAF_OPERATIONS: OperationSeed[] = [
  { id: 'op-birth', name: 'Naissance / adoption', code: 'birth' },
  { id: 'op-wedding', name: 'Mariage / Pacs', code: 'wedding' },
  { id: 'op-retirement', name: 'Départ à la retraite', code: 'retirement' },
  { id: 'op-parents', name: 'Fête des mères / fête des pères', code: 'parents' },
  { id: 'op-catherine', name: 'Sainte-Catherine', code: 'catherine' },
  { id: 'op-nicolas', name: 'Saint-Nicolas', code: 'nicolas' },
  { id: 'op-xmas-parent', name: 'Noël Parent', code: 'xmas-parent' },
  { id: 'op-xmas-child', name: 'Noël Enfant', code: 'xmas-child', collectsChildIdentity: true },
  { id: 'op-school', name: 'Rentrée scolaire', code: 'school', collectsChildIdentity: true }
]

function distributionId(operationId: string) {
  return `dist-${operationId}`
}

function visualFor(operationId: string, name: string) {
  if (operationId === 'op-xmas-child') return 'Visuel carte Noël A'
  if (operationId === 'op-xmas-parent') return 'Visuel carte Noël B'
  return `Visuel ${name}`
}

function withRates(ids: string[]): Record<string, MerchantRate> {
  return ids.reduce((rates, id, index) => {
    rates[id] = { commission: 8 + (index % 3), discount: 5 }
    return rates
  }, {} as Record<string, MerchantRate>)
}

function urssafCard(
  operation: OperationSeed,
  type: MultiChoiceCardModel['type'],
  itemIds: string[],
  defaultActive: boolean
): MultiChoiceCardModel {
  const suffix = type === 'MIXED' ? 'mixed' : type === 'NO_CB' ? 'nocb' : 'cbonly'
  const typeName = type === 'MIXED' ? 'Mixte' : type === 'NO_CB' ? 'Sans CB' : 'CB uniquement'
  return {
    id: `card-${operation.id}-${suffix}`,
    operationModelId: operation.id,
    name: `${operation.name} · ${typeName}`,
    type,
    visual: visualFor(operation.id, operation.name),
    validityMonths: 12,
    urssafEventIds: [operation.id],
    itemIds,
    defaultActive,
    builtin: true
  }
}

export function createSeedState(): BrowseAndGoState {
  const merchants: Merchant[] = BASE_MERCHANTS.map(merchant => ({ ...merchant, categories: [...merchant.categories] }))
  const operations: OperationModel[] = []
  const cards: MultiChoiceCardModel[] = []

  URSSAF_OPERATIONS.forEach(seed => {
    operations.push({
      id: seed.id,
      name: seed.name,
      family: 'URSSAF',
      code: seed.code,
      defaultDurationMonths: 12,
      defaultAudience: 'CSE',
      collectsChildIdentity: Boolean(seed.collectsChildIdentity),
      builtin: true
    })
    merchants.push({
      id: distributionId(seed.id),
      name: `Type de distribution · ${seed.name}`,
      categories: [],
      bank: true
    })
    const distribution = distributionId(seed.id)
    cards.push(urssafCard(seed, 'MIXED', [...URSSAF_EBONS, distribution], true))
    cards.push(urssafCard(seed, 'NO_CB', [...URSSAF_EBONS], false))
    cards.push(urssafCard(seed, 'CB_ONLY', [distribution], true))
  })

  operations.push({
    id: 'op-food',
    name: 'CROUS / Alimentaire',
    family: 'OTHER',
    code: 'food',
    defaultDurationMonths: 12,
    defaultAudience: 'NONE',
    collectsChildIdentity: false,
    builtin: true,
    adminWarning: 'Ce modèle n\'est jamais affecté par défaut. Il s\'ajoute dans les affectations particulières.'
  })
  merchants.push({
    id: distributionId('op-food'),
    name: 'Type de distribution · Restauration',
    categories: ['Restauration'],
    bank: true
  })
  cards.push({
    id: 'card-op-food-mixed',
    operationModelId: 'op-food',
    name: 'Alimentaire · avec CB Restauration',
    type: 'MIXED',
    visual: 'Visuel Alimentaire',
    validityMonths: 12,
    urssafEventIds: [],
    itemIds: ['carrefour', 'cf-cadeau', distributionId('op-food')],
    defaultActive: true,
    builtin: true
  })
  cards.push({
    id: 'card-op-food-nocb',
    operationModelId: 'op-food',
    name: 'Alimentaire · sans CB',
    type: 'NO_CB',
    visual: 'Visuel Alimentaire',
    validityMonths: 12,
    urssafEventIds: [],
    itemIds: ['carrefour', 'cf-cadeau'],
    defaultActive: true,
    builtin: true
  })

  operations.push({
    id: 'op-incentive',
    name: 'Incentive',
    family: 'OTHER',
    code: 'incentive',
    defaultDurationMonths: 12,
    defaultAudience: 'NON_CSE',
    collectsChildIdentity: false,
    builtin: true,
    adminWarning: 'Ce type d\'opération ne doit pas être utilisé pour les dotations URSSAF.'
  })
  merchants.push({
    id: distributionId('op-incentive'),
    name: 'Type de distribution · Incentive',
    categories: [],
    bank: true
  })
  const incentiveItems = ['fnac', 'decathlon', 'sephora', 'ikea', 'cultura']
  cards.push({
    id: 'card-op-incentive-mixed',
    operationModelId: 'op-incentive',
    name: 'Incentive · Mixte',
    type: 'MIXED',
    visual: 'Visuel Incentive',
    validityMonths: 12,
    urssafEventIds: [],
    itemIds: [...incentiveItems, distributionId('op-incentive')],
    defaultActive: true,
    builtin: true
  })
  cards.push({
    id: 'card-op-incentive-nocb',
    operationModelId: 'op-incentive',
    name: 'Incentive · Sans CB',
    type: 'NO_CB',
    visual: 'Visuel Incentive',
    validityMonths: 12,
    urssafEventIds: [],
    itemIds: [...incentiveItems],
    defaultActive: false,
    builtin: true
  })
  cards.push({
    id: 'card-op-incentive-cbonly',
    operationModelId: 'op-incentive',
    name: 'Incentive · CB uniquement',
    type: 'CB_ONLY',
    visual: 'Visuel Incentive',
    validityMonths: 12,
    urssafEventIds: [],
    itemIds: [distributionId('op-incentive')],
    defaultActive: true,
    builtin: true
  })

  const standardIds = ['fnac', 'decathlon', 'sephora', 'ikea', 'cultura', 'leroy', 'galeries', 'redoute', 'kiabi', 'boulanger']
  const belgiumIds = ['decathlon', 'sephora', 'ikea']
  const programs: BenefitProgram[] = [
    {
      id: 'prog-fr',
      name: 'Programme Standard France',
      logo: 'FR',
      isDefault: true,
      merchantIds: standardIds,
      rates: withRates(standardIds),
      builtin: true
    },
    {
      id: 'prog-be',
      name: 'Programme Belgique',
      logo: 'BE',
      isDefault: false,
      merchantIds: belgiumIds,
      rates: withRates(belgiumIds),
      builtin: true
    }
  ]

  const selections: SavedSelection[] = [
    {
      id: 'sel-france',
      name: 'Grandes enseignes France',
      itemIds: ['boulanger', 'carrefour', 'cultura', 'decathlon', 'fnac', 'galeries', 'ikea', 'kiabi', 'leroy', 'redoute', 'sephora']
    },
    {
      id: 'sel-culture',
      name: 'Culture',
      itemIds: ['cultura', 'fnac', 'cf-ticket', 'cf-spectacle', 'ew-culture']
    },
    {
      id: 'sel-food',
      name: 'Alimentation + Restauration',
      itemIds: ['carrefour', 'cf-cadeau', distributionId('op-food')]
    },
    {
      id: 'sel-incentive',
      name: 'Catalogue large Incentive',
      itemIds: merchants.filter(merchant => !merchant.bank).map(merchant => merchant.id)
    }
  ]

  const exceptions: AssignmentException[] = []
  URSSAF_OPERATIONS.forEach(operation => {
    exceptions.push({
      id: `ex-${operation.id}-cbonly`,
      clientId: 'PRO-411',
      objectType: 'MULTICHOICE_CARD_MODEL',
      objectId: `card-${operation.id}-cbonly`,
      action: 'REMOVE'
    })
    exceptions.push({
      id: `ex-${operation.id}-nocb`,
      clientId: 'PRO-411',
      objectType: 'MULTICHOICE_CARD_MODEL',
      objectId: `card-${operation.id}-nocb`,
      action: 'ADD'
    })
  })
  exceptions.push({
    id: 'ex-pro-411-food',
    clientId: 'PRO-411',
    objectType: 'OPERATION_MODEL',
    objectId: 'op-food',
    action: 'ADD'
  })

  return {
    categories: CATEGORIES,
    merchants,
    operations,
    cards,
    programs,
    selections,
    clients: [
      { id: 'PRO-305', name: 'Maison Rivoli', cseOrRelated: false, expertMode: false },
      { id: 'PRO-1042', name: 'CSE Horizon', cseOrRelated: true, expertMode: false },
      { id: 'PRO-411', name: 'CSE Nova Industrie', cseOrRelated: true, expertMode: false },
      { id: 'PRO-526', name: 'Orion Services', cseOrRelated: false, expertMode: false }
    ],
    exceptions
  }
}
